/**
 * The `permanent` filter set for the refresh-jobs table, and the query params behind the tiles.
 *
 * `GET /refresh-jobs` takes six optional filters — `run_id`, `status`, `refresh_outcome`, `mode`,
 * `site`, `admin_event_id` — and **types the three enum-shaped ones**, so an unknown value is a
 * 422 rather than a page that silently stays empty. That is why every sentinel here is `null` and
 * never a placeholder string: `null` means "send nothing", which is the only safe way to clear an
 * enum filter.
 *
 * Same always-emit convention as `buildItemsFilters` and `buildRunsFilters`: every
 * user-controlled field is present on every render, carrying `value: undefined` when cleared,
 * never omitted. Refine builds the query as `unionFilters(permanent, internalState)` and drops
 * undefined entries *after* the dedupe, so a present-but-undefined entry shadows a stale internal
 * copy while an omitted one lets it survive — which is how a table gets stuck on a filter the
 * curator cleared.
 */
import type { CrudFilters } from "@refinedev/core";

import type { ItemStatus, RefreshMode, RefreshOutcome } from "@/types/tina";

export interface RefreshJobsFilterState {
  /** Always set on the run drill-down: the table is scoped to one refresh run. */
  runId: string;
  outcome: RefreshOutcome | null;
  mode: RefreshMode | null;
  status: ItemStatus | null;
  site: string | null;
}

export const buildRefreshJobsFilters = ({
  runId,
  outcome,
  mode,
  status,
  site,
}: RefreshJobsFilterState): CrudFilters =>
  [
    { field: "run_id", operator: "eq", value: runId || undefined },
    { field: "refresh_outcome", operator: "eq", value: outcome ?? undefined },
    { field: "mode", operator: "eq", value: mode ?? undefined },
    { field: "status", operator: "eq", value: status ?? undefined },
    { field: "site", operator: "eq", value: site ?? undefined },
  ] as CrudFilters;

/**
 * The same filter set as query params, for the summary read.
 *
 * `GET /refresh-jobs/summary` takes the identical six filters, so the tile row can be narrowed
 * exactly as the table is. The data provider's `flattenFilters` drops undefined/null values on
 * the list side; this does the same by omission, because the summary goes through `custom()`
 * with a plain params object instead.
 *
 * **`outcome` is deliberately NOT forwarded.** The tiles ARE the outcome breakdown, so passing
 * the selected outcome back into their own query would collapse the row to one non-zero tile the
 * moment a curator clicked it — the tiles must keep showing what the run did while the table
 * below shows the slice.
 */
export function refreshJobsSummaryParams({
  runId,
  mode,
  status,
  site,
}: RefreshJobsFilterState): Record<string, string> {
  const params: Record<string, string> = {};
  if (runId) params.run_id = runId;
  if (mode) params.mode = mode;
  if (status) params.status = status;
  if (site) params.site = site;
  return params;
}

/** The count for one outcome out of a summary's histogram; 0 when the bucket is absent. */
export function outcomeCount(
  outcomes: ReadonlyArray<{ outcome: string | null; count: number }>,
  outcome: RefreshOutcome,
): number {
  return outcomes.find((bucket) => bucket.outcome === outcome)?.count ?? 0;
}

/**
 * Jobs still in flight — the `null` bucket, plus nothing else.
 *
 * Not `total - settled`: the histogram is exhaustive by construction (it is a `GROUP BY` over
 * every matching row), so the NULL bucket already IS the in-flight count, and deriving it by
 * subtraction would turn any new outcome value into a phantom "in flight" number.
 */
export function inFlightCount(
  outcomes: ReadonlyArray<{ outcome: string | null; count: number }>,
): number {
  return outcomes.find((bucket) => bucket.outcome === null)?.count ?? 0;
}
